/**
 * @param {WebGL2RenderingContext} gl
 * @param {string} extension
 */
export function enableExtension(gl, extension) {
  const ext = gl.getExtension(extension);
  if (!ext) {
    console.error(`Extension not supported: ${extension}`);
  }
  return ext;
}

/**
 * @param {WebGL2RenderingContext} gl
 * @param {GLenum} type
 * @param {string} source
 * @returns {WebGLShader}
 */
export function createShader(gl, type, source) {
  const shader = gl.createShader(type);
  if (!shader) {
    throw new Error(`Invalid shader type: ${type}`);
  }
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    throw new Error(gl.getShaderInfoLog(shader) ?? "Failed to compile shader");
  }
  return shader;
}

/**
 * @typedef {WebGLProgram & {
 *  [key: `a${string}`]: GLint;
 *  [key: `u${string}`]: WebGLUniformLocation | null;
 * }} Program
 */

/**
 * @param {WebGL2RenderingContext} gl
 * @param {string} vertexSource
 * @param {string} fragmentSource
 * @param {string[]} [feedbackVaryings]
 * @param {GLenum} [feedbackBufferMode]
 * @returns {Program}
 */
export function createProgram(gl, vertexSource, fragmentSource, feedbackVaryings, feedbackBufferMode) {
  const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexSource);
  const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
  const program = /** @type {Program} */ (gl.createProgram());
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  if (feedbackVaryings) {
    gl.transformFeedbackVaryings(program, feedbackVaryings, feedbackBufferMode ?? gl.INTERLEAVED_ATTRIBS);
  }
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    throw new Error(gl.getProgramInfoLog(program) ?? "Failed to link program");
  }
  const numAttributes = gl.getProgramParameter(program, gl.ACTIVE_ATTRIBUTES);
  for (let index = 0; index < numAttributes; index++) {
    const { name } = /** @type {WebGLActiveInfo} */ (gl.getActiveAttrib(program, index));
    program[/** @type {`a${string}`} */ (name)] = gl.getAttribLocation(program, name);
  }
  const numUniforms = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS);
  for (let index = 0; index < numUniforms; index++) {
    const { name } = /** @type {WebGLActiveInfo} */ (gl.getActiveUniform(program, index));
    program[/** @type {`u${string}`} */ (name)] = gl.getUniformLocation(program, name);
  }
  return program;
}

/**
 * @overload
 * @param {WebGL2RenderingContext} gl
 * @param {GLsizeiptr} data
 * @param {GLenum} usage
 * @returns {WebGLBuffer}
 */
/**
 * @overload
 * @param {WebGL2RenderingContext} gl
 * @param {ArrayBuffer | ArrayBufferView | null} data
 * @param {GLenum} usage
 * @returns {WebGLBuffer}
 */
/**
 * @param {WebGL2RenderingContext} gl
 * @param {any} data
 * @param {GLenum} usage
 * @returns {WebGLBuffer}
 */
export function createBuffer(gl, data, usage) {
  const b = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, b);
  gl.bufferData(gl.ARRAY_BUFFER, data, usage);
  gl.bindBuffer(gl.ARRAY_BUFFER, null);
  return b;
}

/**
 * @typedef {object} Attribute
 * @property {WebGLBuffer} buffer
 * @property {GLuint} index
 * @property {GLint} size
 * @property {GLenum} type
 * @property {GLboolean} [normalized]
 * @property {GLsizei} [stride]
 * @property {GLintptr} [offset]
 */

/**
 * @param {WebGL2RenderingContext} gl
 * @param {Attribute[]} attributes
 * @returns {WebGLVertexArrayObject}
 */
export function createVertexArray(gl, attributes) {
  const a = gl.createVertexArray();
  gl.bindVertexArray(a);
  for (const { buffer, index, size, type, normalized = false, stride = 0, offset = 0 } of attributes) {
    if (index == null) continue;
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.enableVertexAttribArray(index);
    gl.vertexAttribPointer(index, size, type, normalized, stride, offset);
  }
  gl.bindBuffer(gl.ARRAY_BUFFER, null);
  gl.bindVertexArray(null);
  return a;
}

/**
 * @param {WebGL2RenderingContext} gl
 * @param {number} width
 * @param {number} height
 * @returns {WebGLTexture}
 */
export function createTexture(gl, width, height) {
  const texture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
  gl.bindTexture(gl.TEXTURE_2D, null);
  return texture;
}

/**
 * @param {WebGL2RenderingContext} gl
 * @param {number} w
 * @param {number} h
 */
export function resizeTexture(gl, tex, w, h) {
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
  gl.bindTexture(gl.TEXTURE_2D, null);
}

/**
 * @param {WebGL2RenderingContext} gl
 * @param {WebGLTexture} texture
 * @returns {WebGLFramebuffer}
 */
export function createFramebuffer(gl, texture) {
  const fbo = gl.createFramebuffer();
  gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);

  if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
    console.error("Framebuffer not complete");
  }

  gl.clearColor(0, 0, 0, 1);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  return fbo;
}

/**
 * @param {WebGL2RenderingContext} gl
 * @param {{index: GLuint, buffer?: WebGLBuffer}[]} buffers
 * @returns {WebGLTransformFeedback}
 */
export function createTransformFeedback(gl, buffers) {
  const tf = gl.createTransformFeedback();
  gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, tf);
  for (const { index, buffer = null } of buffers) {
    gl.bindBufferBase(gl.TRANSFORM_FEEDBACK_BUFFER, index, buffer);
  }
  gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, null);
  return tf;
}

/**
 *
 * @param {WebGL2RenderingContext} gl
 * @param {WebGLProgram} program
 * @param {WebGLVertexArrayObject} vao
 */
export function useProgram(gl, program, vao) {
  gl.useProgram(program);
  gl.bindVertexArray(vao);
}

/**
 *
 * @param {WebGL2RenderingContext} gl
 * @param {number} index
 * @param {WebGLUniformLocation?} location
 * @param {WebGLTexture} texture
 */
export function uniformTexture(gl, index, location, texture) {
  gl.activeTexture(gl.TEXTURE0 + index);
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.uniform1i(location, index);
}

/**
 *
 * @param {WebGL2RenderingContext} gl
 * @param {GLenum} mode
 * @param {WebGLTransformFeedback?} feedback
 * @param {() => void} callback
 */
export function withTransformFeedback(gl, mode, feedback, callback) {
  gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, feedback);
  gl.beginTransformFeedback(mode);
  callback();
  gl.endTransformFeedback();
  gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, null);
}

/**
 *
 * @param {WebGL2RenderingContext} gl
 * @param {string} url
 * @returns {WebGLTexture}
 */
export function loadTexture(gl, url, wrap) {
  const texture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, texture);

  const level = 0;
  const internalFormat = gl.RGBA;
  const width = 1;
  const height = 1;
  const border = 0;
  const srcFormat = gl.RGBA;
  const srcType = gl.UNSIGNED_BYTE;
  const pixel = new Uint8Array([0, 0, 255, 255]); // opaque blue
  gl.texImage2D(
    gl.TEXTURE_2D,
    level,
    internalFormat,
    width,
    height,
    border,
    srcFormat,
    srcType,
    pixel,
  );

  if (url) {
    const image = new Image();
    image.onload = () => {
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texImage2D(
        gl.TEXTURE_2D,
        level,
        internalFormat,
        srcFormat,
        srcType,
        image,
      );

      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, wrap ?? gl.REPEAT);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, wrap ?? gl.REPEAT);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      texture.image = image;
    };
    image.src = url;
  }

  return texture;
}
